import React, { useEffect, useState, useRef, lazy, createElement } from 'react';
import { useRevealAnimation } from '../utils/IntersectionObserverUtil';
export function BlinkTestSection() {
  // Use centralized reveal animation
  useRevealAnimation();
  // Video loading state and ref
  const videoRef = useRef<HTMLDivElement>(null);
  const [videoLoaded, setVideoLoaded] = useState(false);
  // Lazy load video thumbnail and prepare for click-to-play
  useEffect(() => {
    if (!videoRef.current) return;
    const options = {
      root: null,
      rootMargin: '200px',
      threshold: 0.1
    };
    const handleIntersection = (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting && !videoLoaded) {
        // First load just the thumbnail
        const img = document.createElement('img');
        img.src = 'https://i.ytimg.com/vi/xGTa2mlU8Cg/maxresdefault.jpg';
        img.alt = 'Blink test video thumbnail';
        img.className = 'absolute top-0 left-0 w-full h-full object-cover rounded-lg';
        img.width = 640;
        img.height = 360;
        img.loading = 'lazy';
        img.decoding = 'async';
        if (videoRef.current) {
          videoRef.current.appendChild(img);
          // Add play button overlay
          const playButton = document.createElement('div');
          playButton.className = 'absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 cursor-pointer rounded-lg';
          const playIcon = document.createElement('div');
          playIcon.className = 'bg-white bg-opacity-80 text-teal-600 rounded-full p-3 shadow-lg';
          playIcon.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-play"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>`;
          playButton.appendChild(playIcon);
          videoRef.current.appendChild(playButton);
          // Add click event to load and play the actual video
          playButton.addEventListener('click', () => {
            if (!videoRef.current) return;
            // Remove thumbnail and play button
            videoRef.current.innerHTML = '';
            // Create and add iframe
            const iframe = document.createElement('iframe');
            iframe.className = 'absolute top-0 left-0 w-full h-full rounded-lg';
            iframe.src = 'https://cdn.shopify.com/videos/c/o/v/0d498b4916ab4b6880ec5a17fcfceae8.mp4';
            iframe.title = 'Blink Test Video';
            iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
            iframe.frameBorder = '0';
            iframe.loading = 'lazy';
            videoRef.current.appendChild(iframe);
          });
          setVideoLoaded(true);
          observer.disconnect();
        }
      }
    };
    const observer = new IntersectionObserver(handleIntersection, options);
    observer.observe(videoRef.current);
    return () => {
      observer.disconnect();
    };
  }, [videoLoaded]);
  return <section className="py-12 border-b border-gray-200" data-id="element-26">
      <div className="bg-blue-50 p-6 rounded-lg border border-blue-100 mb-6" data-id="element-27">
        <h3 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-28">
          The Blink Test
        </h3>
        <p className="text-gray-700 mb-4" data-id="element-29">Try this quick experiment right now:</p>
        {/* Video section with performance optimizations */}
        <div className="mb-6" data-id="element-30">
          <div className="bg-gray-100 p-3 rounded-lg shadow-sm mb-3" data-id="element-31">
            <p className="text-center text-gray-700 font-medium" data-id="element-32">
              <span className="text-teal-600" data-id="element-33">▶️</span> Watch this short video
              to see if your eyes pass the test
            </p>
          </div>
          <div className="relative aspect-video w-full rounded-lg overflow-hidden shadow-md" style={{
          backgroundColor: '#000',
          minHeight: '200px'
        }} ref={videoRef} data-id="element-34">
            {/* Video will be loaded here via intersection observer */}
          </div>
          <p className="text-xs text-gray-500 text-center mt-2 italic" data-id="element-35">
            The Blink Test has been used for over 100 years by eye experts to quickly spot imbalances that make eyes feel irritated, tired, or watery.
          </p>
        </div>
        <ol className="list-decimal pl-5 space-y-2 text-gray-700" data-id="element-36">
          <li className="reveal-on-scroll" data-id="element-37">
            Blink normally, then keep your eyes open and start counting.
          </li>
          <li className="reveal-on-scroll" data-id="element-38">
            Notice when you first feel the urge to blink again.
          </li>
          <li className="reveal-on-scroll" data-id="element-39">
            If it's less than 7-10 seconds, your tear film is evaporating too
            quickly—a sign of oil layer problems.
          </li>
        </ol>
        <div className="max-w-md mx-auto bg-blue-50 border border-blue-200 p-4 mt-6 rounded-lg shadow-sm text-left" data-id="element-40">
  <p className="text-gray-800 text-sm md:text-base leading-relaxed mb-3" data-id="element-41">
    <span className="font-semibold" data-id="element-42">Most people don’t realize</span> every blink is supposed 
    to release a thin protective layer that keeps eyes comfortable.
  </p>
  <p className="text-gray-800 text-sm md:text-base leading-relaxed mb-3" data-id="element-43">
    But endless screens, dry indoor air, and natural changes with age can throw that system off balance.
  </p>
      </div>
      </div>
    </section>;
}